import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PartProperty, PlacementType } from "./models/parts";
import { UPLOAD_BOARD } from "../constants/routes";
import { OrderState } from "../components/Orders/constants";
import { BoardView } from "../utils/models/boardToolSteps";
import {
  BOMUpdate,
  CustomerSpecificationsUpdate,
  ShipmentUpdate,
} from "./models/boardUpdates";
import { PickupInfo, ShippingRate } from "./shoppingCartOrder";

export const EMPTY_ADDRESS = {
  name: "",
  company_name: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  postal_code: "",
  phone_number: "",
  address_index: null,
};

export enum BoardState {
  ACTIVE = "Active",
  READY_TO_DELETE = "Ready to delete",
}

export enum PhysicalDetailsState {
  UNPROCESSED = "Unprocessed",
  PROCESSING = "Processing",
  PROCESSED = "Processed",
}

export enum BOMState {
  UNPROCESSED = "Unprocessed",
  PROCESSING = "Processing",
  PROCESSED = "Processed",
}

export const BOM_PROCESSING_STATES = new Set([
  BOMState.UNPROCESSED,
  BOMState.PROCESSING,
]);

export const boardHasBeenPaidFor = (board: Board) => {
  return board.quote.state == QuoteState.ORDER_RECEIVED;
};

export interface BOMFileInfo {
  file_name: string;
  header_row_index?: number;
  designator_column?: string;
  mpn_column?: string;
  manufacturer_column?: string;
  footprint_column?: string;
  value_text_column?: string;
}

export interface PNPFileInfo {
  file_name: string;
  header_row_index?: number;
  designator_column?: string;
  x_column?: string;
  y_column?: string;
  rotation_column?: string;
  mount_type_column?: string;
  unit?: string;
}

export interface GerberFileLayerMapping {
  [layerName: string]: string[] | string;
}

export interface UploadConfig {
  gerber_file_layer_mapping: null | GerberFileLayerMapping;
  bom_file_info: null | BOMFileInfo;
  pnp_file_info: null | PNPFileInfo;
  assembly_drawing: null | string[];
  fab_drawing: null | string[];
  reference_file: null | string[];
}

export interface BOMFile {
  url: string;
  file_name: string;
}

export interface BOMFileSet {
  bom: BOMFile | null;
  pnp: BOMFile | null;
  uploaded_at: string;
  uploaded_by: string | null;
}

export enum PartShoppingListStatus {
  PROCESSING = "Processing",
  CREATED = "Created",
}

export interface PartShoppingList {
  shipment_id: string;
  spreadsheet_link: string;
}

export interface PartShoppingLists {
  lists: PartShoppingList[] | null;
  date_created: string | null;
  status: PartShoppingListStatus;
}

export interface GerberZip {
  url: string;
  file_name: string;
}

export interface GerberUploadInfo {
  uploaded_at: string;
  uploaded_by: string | null;
  gerber_zip: GerberZip;
}

export interface BoardDownloadFiles {
  brd?: string;
  sch?: string;
  kicad_zip?: string;
  latest_unprocessed_gerber?: string;
  gerber_uploads: GerberUploadInfo[];
  drc_output_gerber?: string;
  additional_bom_files: BOMFileSet[];
  part_shopping_lists: PartShoppingLists;
}

export enum CheckResultStatus {
  WARNING = "Warning",
  ERROR = "Error",
  DISMISSED = "Dismissed",
}

export interface CheckResult {
  result_id: string;
  status: CheckResultStatus;
  message: string;
  more_info_link: string | null;
  layer: string | null;
  shape_svg_url: string | null;
  locations: number[][] | null;
  location_unit: string;
}

export interface PartSpecification {
  mpn: string | null;
  manufacturer: string | null;
}

export enum PartPlacementCheckState {
  SHOULD_CHECK = "Should check",
  NEED_NOT_CHECK = "Need not check",
  FAILED = "Failed",
  PASSED = "Passed",
  MISSING_FOOTPRINT_INFO = "Missing footprint info",
  MISSING_PLACEMENT_INFO = "Missing placement info",
  CANNOT_CHECK = "Cannot check",
}

export interface PartPlacementCheck {
  state: PartPlacementCheckState;
  match_score: number | null;
  match_threshold: number | null;
}

export const DEFAULT_PART_PLACEMENT_CHECK: PartPlacementCheck = {
  state: PartPlacementCheckState.SHOULD_CHECK,
  match_score: null,
  match_threshold: null,
};

export enum PartSelectionCheckState {
  UNCHECKED = "Unchecked",
  CHECKED = "Checked",
}

export interface PartSelectionCheck {
  state: PartSelectionCheckState;
  message: string | null;
}

export const DEFAULT_PART_SELECTION_CHECK: PartSelectionCheck = {
  state: PartSelectionCheckState.UNCHECKED,
  message: null,
};

export interface BillableElement {
  id: string;
  name: string;
  value_text: string;
  footprint_name: string;
  footprint_group_key_component: string;
  properties: PartProperty[];
  do_not_populate: boolean;
  part_id: string | null;
  placement: ElementPlacement | null;
  part_specification: PartSpecification | null;
  is_consignment: boolean;
  lock_allocation: boolean;
  group_key: string;
  part_placement_check: PartPlacementCheck;
  part_selection_check: PartSelectionCheck;
}

export interface BillOfMaterials {
  elements: BillableElement[];
  state: BOMState;
}

export enum CADSources {
  GERBER = "Gerber",
  KICAD = "KiCad",
}

export const PublicCADSources = [CADSources.KICAD];

export const uploadBoardForCad = (cadSource?: CADSources) => {
  if (cadSource !== undefined) {
    return `${UPLOAD_BOARD}?cad=${cadSource.toLowerCase()}`;
  }
  return UPLOAD_BOARD;
};

export interface ImageSet {
  detail: string | null;
  preview: string | null;
  svg: string | null;
  name: string | null;
}

export interface FabDrawing {
  part_placement_type: string;
  top_assembly: ImageSet | null;
  bottom_assembly: ImageSet | null;
}

export enum FabDrawingsStatus {
  PROCESSING = "Processing",
  DRAWN = "Drawn",
}

export enum BoardRevisionState {
  UNPROCESSED = "Unprocessed",
  PROCESSING = "Processing",
  PROCESSED = "Processed",
}

export interface FabDrawings {
  drawings: FabDrawing[];
  date_created: string;
  status: FabDrawingsStatus;
}

export interface BoardImages {
  top: ImageSet | null;
  bottom: ImageSet | null;
  layers: ImageSet[];
  fab_drawings: FabDrawings;
}

export const LEADED_SOLDER = "Leaded solder";

export interface Dimensions {
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
  unit: string;
}

export interface PhysicalDetails {
  num_layers: number | null;
  dimensions: Dimensions | null;
  bounding_box_width: number;
  bounding_box_height: number;
  bounding_box_unit: string;
  outer_copper_weight: number;
  inner_copper_weight: number;
  copper_weight_unit: string;
  substrate: string;
  surface_finish: string;
  soldermask_color: string;
  solder_type: string;
  substrate_thickness: number;
  substrate_thickness_unit: string;
  min_hole_diameter: number | null;
  min_hole_diameter_unit: string | null;
}

export interface OrderSummary {
  id: string;
  state: OrderState;
}

export enum ManufacturingStepStatus {
  NOT_APPLICABLE = "Not applicable",
  WAITING = "Waiting",
  READY = "Ready",
  COMPLETE = "Complete",
}

export enum ManufacturingStep {
  SMT_BOTTOM = "smt_bottom",
  SMT_TOP = "smt_top",
  HAND_SOLDERING = "hand_soldering",
  INSPECTION = "inspection",
  CLEANING = "cleaning",
  COATING = "coating",
  PACKAGING = "packaging",
}

export interface ManufacturingSteps {
  boards_purchased: ManufacturingStepStatus;
  stencils_purchased: ManufacturingStepStatus;
  parts_purchased: ManufacturingStepStatus;
  placement_checks: ManufacturingStepStatus;
  programmed: ManufacturingStepStatus;
  smt_bottom: ManufacturingStepStatus;
  smt_top: ManufacturingStepStatus;
  hand_soldering: ManufacturingStepStatus;
  inspection: ManufacturingStepStatus;
  cleaning: ManufacturingStepStatus;
  coating: ManufacturingStepStatus;
  packaging: ManufacturingStepStatus;
}

export interface InventoryStatus {
  stencil: ManufacturingStepStatus;
  panel: ManufacturingStepStatus;
}

export interface ManufacturingChecklist {
  num_parts_missing_placement_type: number;
  steps: ManufacturingSteps;
  inventory_status: InventoryStatus;
}

export interface Fiducial {
  x: number;
  y: number;
}

interface Fiducials {
  x_offset: number;
  top_right_x_offset: number;
  y_offset: number;
  top_locations: Fiducial[];
  bottom_locations: Fiducial[];
}

export interface PickAndPlace {
  left_rail_width: number;
  right_rail_width: number;
  top_rail_width: number;
  bottom_rail_width: number;

  num_rows: number;
  row_spacing: number;
  num_columns: number;
  column_spacing: number;

  x_offset: number;
  y_offset: number;
  panel_width: number;
  panel_height: number;
  pitch_width: number;
  pitch_height: number;
  fiducials: Fiducials;
  unit: string;
  rotation_degrees: number;
}

export enum StencilType {
  TOP = "Top",
  BOTTOM = "Bottom",
  DUAL_IMAGE = "Dual image",
}

export enum BoardMountTypes {
  TOP_ONLY = "Top only",
  BOTTOM_ONLY = "Bottom only",
  TOP_AND_BOTTOM = "Top and Bottom",
}

export interface StencilContainer {
  flashpcb_container_barcode: string;
  stencil_type: StencilType;
  flashpcb_bin_id: string | null;
  flashpcb_bin_barcode: string | null;
}

export interface StencilDetails {
  board_mount_types: BoardMountTypes;
  stencil_ids: string[];
}

export interface GerberFileIssue {
  filename: string;
  diff_score: number;
  diff_score_threshold: number;
}

export interface ManufacturingNotification {
  gerber_file_issue: GerberFileIssue | null;
}

export interface ManufacturingDetails {
  pick_and_place: PickAndPlace;
  stencil_details: StencilDetails;
  panel_details: string[];
  notifications: ManufacturingNotification[];
  customer_specifications: CustomerSpecifications;
}

export interface ShipmentPart {
  part_name: string;
  part_id: string;
  num_elements: number;
  num_elements_per_shipment: number;
  element_names: string[];
  element_ids: string[];
  num_through_hole_joints_per_package: number;
  requires_stencil: boolean;
  paste_in_hole: boolean;
  cannot_clean: boolean;
  is_consignment: boolean;
  lock_allocation: boolean;
  required_amount: number;
  // As of when the shipment was fetched. Pages that show allocations live read
  // them from the part instead.
  amount_allocated: number;
  part_may_require_a_tray: boolean;
  contains_lithium: boolean;
  // The shipment's own placement type where it has one, else the part's
  placement_type: PlacementType | null;
}

export enum LeadTimeDays {
  THREE = 3,
  FIVE = 5,
  TEN = 10,
}

export interface DeliveryInfo {
  order_owner_id: string | null;
  shipping_address: ShippingAddress | null;
  shipping_rate_specified_in_order: ShippingRate | null;
  shipping_rate: ShippingRate | null;
  pickup_info: PickupInfo | null;
}

export enum ShipmentState {
  UNPROCESSED = "Unprocessed",
  PROCESSING = "Processing",
  PROCESSED = "Processed",
  DELETED = "Deleted",
}

export enum ProductionStatus {
  QUOTING = "Quoting",
  MANUFACTURING = "Manufacturing",
  MANUFACTURED = "Manufactured",
  CANCELED = "Canceled",
}

export interface ShipmentsResponse {
  shipments: Shipment[];
}

export interface Shipment {
  id: string;
  board_id: string;
  shipment_parts: ShipmentPart[];
  num_boards: number;
  lead_time_days: number | null;
  base_lead_time_days: LeadTimeDays;
  ship_date: string;
  manufacturing_checklist: ManufacturingChecklist;
  lead_time_options: LeadTimeOptions;
  delivery_info: DeliveryInfo;
  state: ShipmentState;
  production_status: ProductionStatus;
  requires_stencil: boolean;
  has_consigned_parts: boolean;
  old_shipment_id: string | null;
}

type Shipments = Record<string, Shipment>;

export interface CustomerSpecifications {
  xray_inspection: boolean;
  cleaning: boolean;
  notes: string;
  conformal_coating: boolean;
  include_date_code: boolean;
}

export interface Board {
  id: string;
  project_name: string;
  internal_nickname: string;
  user_id: string | null;
  state: BoardState;
  physical_details_state: PhysicalDetailsState;
  cad_source: string;
  datetime_uploaded: string;
  physical_details: PhysicalDetails | null;
  manufacturing_details: ManufacturingDetails;
  file_check_results: CheckResult[];
  panel_check_results: CheckResult[];

  quote: Quote;
  order_summary: OrderSummary | null;
  top_preview: string | null;
  bottom_preview: string | null;
  upload_config: UploadConfig;
}

export interface SelectedElement {
  elementId: string | null;
}

export interface ShippingAddress {
  name: string;
  company_name: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  postal_code: string;
  phone_number: string;
  email?: string;
  address_index: number | null;
}

export type CURRENCY = "USD";

export interface LeadTimeOption {
  base_days: LeadTimeDays;
  days: number;
  ship_date: string;
  disabled: boolean;
}

export interface Closure {
  date: string;
  description: string;
}

export interface LeadTimeOptions {
  options: LeadTimeOption[];
  dates_closed: Closure[];
}

export interface QuoteIssue {
  message: string;
  part_id: string | null;
}

export enum QuoteState {
  UPDATING_QUOTE = "Updating quote",
  QUOTED = "Quoted",
  IN_SHOPPING_CART = "In shopping cart",
  ORDER_RECEIVED = "Order received",
}

export interface QuoteItem {
  id: string;
  amount: number;
  name: string;
  description: string;
  children: QuoteItem[];
}

export interface Quote {
  state: QuoteState;
  currency: CURRENCY;
  total_per_board: number | null;
  sub_total: number | null;
  tariff: number | null;
  total: number | null;
  estimated_total: number | null;
  expires_at: string | null;
  requested_custom_quote: boolean;
  num_boards: number;
  items: QuoteItem[];
  issues: QuoteIssue[];
}

export enum MountType {
  TOP = "Top",
  BOTTOM = "Bottom",
}

export interface ElementPlacement {
  x: number;
  y: number;
  unit: string;
  rotation_degrees: number;
  mount_type: MountType;
}

export interface BoardElementAddition {
  name: string;
  valueText: string;
  doNotPopulate: boolean;
  part_id: string | null;
  isConsignment: boolean;
  elementPlacement: ElementPlacement | null;
}

export interface BoardElementUpdate {
  elementId: string;
  elementName: string;
  doNotPopulate: boolean;
  part_id: string | null;
  isConsignment: boolean;
  lockAllocation: boolean;
  // A null elementPlacement means there will be no update to the element placement
  elementPlacement: ElementPlacement | null;
}

export interface BoardElementToDelete {
  elementId: string;
  elementName: string;
}

type PartSelectionCheckStates = Record<string, PartSelectionCheckState>;

export enum BomDiffStatus {
  ADDITION = "Addition",
  UPDATE = "Update",
  NO_CHANGE = "No change",
  DELETION = "Deletion",
}

export interface BomElementState {
  id: string;
  value_text: string;
  do_not_populate: boolean;
  is_consignment: boolean;
  part_id: string | null;
  placement: ElementPlacement | null;
}

export interface BomDiffItem {
  name: string;
  previous_state: BomElementState | null;
  state: BomElementState | null;
  diff_status: BomDiffStatus;
}

export enum LayerDiffStatus {
  ADDITION = "Addition",
  UPDATE = "Update",
  NO_CHANGE = "No change",
  DELETION = "Deletion",
}

export interface LayerDiff {
  name: string;
  diff_image: ImageSet;
  diff_status: LayerDiffStatus;
}

export interface PhysicalDetailsDiff {
  layers: LayerDiff[];
}

export interface DiffOffset {
  x_mm: number;
  y_mm: number;
}

export interface BoardRevisionDiff {
  previous_board_offset: DiffOffset;
  bom: BomDiffItem[];
  physical_details: PhysicalDetailsDiff;
}

export interface BoardRevisions {
  state: BoardRevisionState;
  previous_revision_id: string | null;
  diff: BoardRevisionDiff | null;
}

export interface ShipmentStateContext {
  state: ShipmentState;
  refresh_datetime: string | null;
  // Moves on every save of the shipment, including transitions that leave its
  // state where it was
  datetime_updated: string;
}

type ShipmentStates = Record<string, ShipmentStateContext>;

export interface BoardMachinesStates {
  datetime_updated: string;
  board_state: BoardState;
  bom_state: BOMState;
  bom_datetime_updated: string;
  physical_details_state: PhysicalDetailsState;
  physical_details_datetime_updated: string | null;
  quote_state: QuoteState;
  quote_datetime: string | null;
  shipment_states: ShipmentStates;
  fab_drawing_status: FabDrawingsStatus;
  board_revision_state: BoardRevisionState;
  part_selection_check_states: PartSelectionCheckStates;
}

export interface BoardContext {
  board: Board | null;
  shipments: Shipments;
  images: BoardImages | null;
  billOfMaterials: BillOfMaterials | null;
  boardRevisions: BoardRevisions | null;
  errored: boolean;
  notFound: boolean;
  view: BoardView | null;
  bomUpdates: BOMUpdate[];
  customerSpecificationsUpdate: CustomerSpecificationsUpdate | null;
  sendingBoardEvent: boolean;
  isProcessing: boolean;
  // These states should only be accessed in the useBoardAndStates hook
  states: BoardMachinesStates | null;
}

type BoardRepository = { [id: string]: BoardContext | null };

export interface AppBoardContext {
  boards: BoardRepository;
}

function getDefaultBoardContext(): BoardContext {
  return {
    board: null,
    shipments: {},
    images: null,
    billOfMaterials: null,
    boardRevisions: null,
    errored: false,
    notFound: false,
    view: null,
    bomUpdates: [],
    customerSpecificationsUpdate: null,
    sendingBoardEvent: false,
    isProcessing: false,
    states: null,
  };
}

export function getContextForBoardId(
  state: AppBoardContext,
  boardId: string,
  setDefaultIfMissing: boolean = false,
): BoardContext {
  const boardContext = state.boards[boardId];
  if (boardContext) {
    return boardContext;
  }
  const defaultBoardContext = getDefaultBoardContext();
  if (setDefaultIfMissing) {
    state.boards[boardId] = defaultBoardContext;
  }
  return defaultBoardContext;
}

function isProcessing(states: BoardMachinesStates) {
  if (
    states.quote_state == QuoteState.UPDATING_QUOTE ||
    states.bom_state == BOMState.PROCESSING ||
    states.physical_details_state == PhysicalDetailsState.PROCESSING ||
    Object.values(states.shipment_states).some((shipmentStateContext) => {
      return shipmentStateContext.state == ShipmentState.PROCESSING;
    }) ||
    states.fab_drawing_status == FabDrawingsStatus.PROCESSING
  ) {
    return true;
  }
  if (states.quote_datetime !== null) {
    const quotedUpdated = new Date(states.quote_datetime);
    const bomUpdated = new Date(states.bom_datetime_updated);
    if (bomUpdated > quotedUpdated) {
      return true;
    }
    if (
      states.physical_details_datetime_updated !== null &&
      new Date(states.physical_details_datetime_updated) > quotedUpdated
    ) {
      return true;
    }
    if (
      Object.values(states.shipment_states).some((shipmentStateContext) => {
        return (
          shipmentStateContext.refresh_datetime !== null &&
          new Date(shipmentStateContext.refresh_datetime) > quotedUpdated
        );
      })
    ) {
      return true;
    }
  }
  return false;
}

export const uploadedBoardSlice = createSlice({
  name: "uploadedBoard",
  initialState: {
    boards: {} as BoardRepository,
  } as AppBoardContext,
  reducers: {
    clearBoards: (state) => {
      state.boards = {};
    },
    clearBoard: (state, action: PayloadAction<{ boardId: string }>) => {
      state.boards[action.payload.boardId] = null;
    },
    boardLoaded: (state, action: PayloadAction<{ board: Board }>) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.board.id,
        true,
      );
      boardContext.board = action.payload.board;
      boardContext.notFound = false;
      boardContext.errored = false;
    },
    imagesLoaded: (
      state,
      action: PayloadAction<{ boardId: string; images: BoardImages }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      boardContext.images = action.payload.images;
    },
    billOfMaterialsLoaded: (
      state,
      action: PayloadAction<{
        boardId: string;
        billOfMaterials: BillOfMaterials;
      }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      boardContext.billOfMaterials = action.payload.billOfMaterials;
    },
    updateFabDrawingsState: (
      state,
      action: PayloadAction<{
        boardId: string;
        fabDrawingsStatus: FabDrawingsStatus;
      }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      if (
        boardContext.images !== null &&
        boardContext.images.fab_drawings !== null
      ) {
        boardContext.images.fab_drawings.status =
          action.payload.fabDrawingsStatus;
      }
    },
    updateBOMState: (
      state,
      action: PayloadAction<{ boardId: string; bomState: BOMState }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      if (boardContext.billOfMaterials !== null) {
        boardContext.billOfMaterials.state = action.payload.bomState;
      }
    },
    physicalDetailsLoaded: (state, action: PayloadAction<{ board: Board }>) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.board.id,
        true,
      );
      if (boardContext.board !== null) {
        boardContext.board.physical_details_state =
          action.payload.board.physical_details_state;
        boardContext.board.physical_details =
          action.payload.board.physical_details;
        boardContext.board.manufacturing_details =
          action.payload.board.manufacturing_details;
        boardContext.board.file_check_results =
          action.payload.board.file_check_results;
        boardContext.board.panel_check_results =
          action.payload.board.panel_check_results;
        boardContext.board.top_preview = action.payload.board.top_preview;
        boardContext.board.bottom_preview = action.payload.board.bottom_preview;
      }
    },
    dismissDesignRuleCheck: (
      state,
      action: PayloadAction<{ boardId: string; resultId: string }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      if (boardContext.board !== null) {
        boardContext.board.panel_check_results =
          boardContext.board.panel_check_results.map((result) => {
            if (result.result_id == action.payload.resultId) {
              return {
                ...result,
                status: CheckResultStatus.DISMISSED,
              };
            }
            return result;
          });
      }
    },
    updateGerberLayerMapping: (
      state,
      action: PayloadAction<{
        boardId: string;
        gerberFileLayerMapping: GerberFileLayerMapping;
      }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      if (boardContext.board !== null) {
        boardContext.board.upload_config.gerber_file_layer_mapping =
          action.payload.gerberFileLayerMapping;
      }
    },
    updatePhysicalDetailsState: (
      state,
      action: PayloadAction<{
        boardId: string;
        physicalDetailsState: PhysicalDetailsState;
      }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      if (boardContext.board !== null) {
        boardContext.board.physical_details_state =
          action.payload.physicalDetailsState;
      }
    },
    boardRevisionLoaded: (
      state,
      action: PayloadAction<{
        boardId: string;
        boardRevisions: BoardRevisions;
      }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      boardContext.boardRevisions = action.payload.boardRevisions;
    },
    updateBoardRevisionState: (
      state,
      action: PayloadAction<{
        boardId: string;
        boardRevisionState: BoardRevisionState;
      }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      if (boardContext.boardRevisions !== null) {
        boardContext.boardRevisions.state = action.payload.boardRevisionState;
      }
    },
    quoteLoaded: (state, action: PayloadAction<{ board: Board }>) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.board.id,
        true,
      );
      if (boardContext.board !== null) {
        boardContext.board.quote = action.payload.board.quote;
      }
    },
    shipmentLoaded: (
      state,
      action: PayloadAction<{ boardId: string; shipment: Shipment }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      boardContext.shipments[action.payload.shipment.id] =
        action.payload.shipment;
    },
    shipmentsLoaded: (
      state,
      action: PayloadAction<{ boardId: string; shipments: Shipment[] }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      action.payload.shipments.forEach((shipment) => {
        boardContext.shipments[shipment.id] = shipment;
      });
    },
    updateCustomQuoteRequest: (
      state,
      action: PayloadAction<{ boardId: string; requested: boolean }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      if (boardContext.board !== null) {
        boardContext.board.quote.requested_custom_quote =
          action.payload.requested;
      }
    },
    updateQuoteState: (
      state,
      action: PayloadAction<{ boardId: string; quoteState: QuoteState }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      if (boardContext.board !== null) {
        boardContext.board.quote.state = action.payload.quoteState;
      }
    },
    updateShipmentsState: (
      state,
      action: PayloadAction<{
        boardId: string;
        shipmentId: string;
        shipmentsState: ShipmentState;
      }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      const shipment = boardContext.shipments[action.payload.shipmentId];
      if (shipment !== undefined) {
        shipment.state = action.payload.shipmentsState;
      }
    },
    updateManufacturingStep: (
      state,
      action: PayloadAction<{
        boardId: string;
        update: {
          shipment_id: string;
          step_status_update: {
            step_name: string;
            step_status: ManufacturingStepStatus;
          }[];
        };
      }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      const matchingShipment =
        boardContext.shipments[action.payload.update.shipment_id];
      if (matchingShipment !== undefined) {
        action.payload.update.step_status_update.forEach((statusUpdate) => {
          matchingShipment.manufacturing_checklist.steps[
            statusUpdate.step_name as keyof ManufacturingSteps
          ] = statusUpdate.step_status;
        });
      }
    },
    boardStatesLoaded: (
      state,
      action: PayloadAction<{ boardId: string; states: BoardMachinesStates }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      boardContext.states = action.payload.states;
      boardContext.isProcessing = isProcessing(action.payload.states);
    },
    setSendingBoardEvent: (
      state,
      action: PayloadAction<{ boardId: string; sending: boolean }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      boardContext.sendingBoardEvent = action.payload.sending;
    },
    addUserToBoard: (
      state,
      action: PayloadAction<{ boardId: string; userId: string }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      if (boardContext.board !== null) {
        boardContext.board.user_id = action.payload.userId;
      }
    },
    pageFailure: (state, action: PayloadAction<{ boardId: string }>) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      boardContext.errored = true;
    },
    pageFailureResolved: (
      state,
      action: PayloadAction<{ boardId: string }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      boardContext.errored = false;
    },
    boardNotFound: (state, action: PayloadAction<{ boardId: string }>) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      boardContext.notFound = true;
      boardContext.errored = false;
    },
    setView: (
      state,
      action: PayloadAction<{ boardId: string; view: BoardView }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      boardContext.view = action.payload.view;
    },
    addBOMUpdates: (
      state,
      action: PayloadAction<{ boardId: string; bomUpdates: BOMUpdate[] }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      boardContext.bomUpdates = [
        ...boardContext.bomUpdates,
        ...action.payload.bomUpdates,
      ];
    },
    removeBOMUpdates: (
      state,
      action: PayloadAction<{ boardId: string; bomUpdates: BOMUpdate[] }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      const idsToRemove = new Set(
        action.payload.bomUpdates.map((update) => update.id),
      );
      boardContext.bomUpdates = boardContext.bomUpdates.filter((update) => {
        return !idsToRemove.has(update.id);
      });
    },
    setProjectName: (
      state,
      action: PayloadAction<{ boardId: string; projectName: string }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      if (boardContext.board !== null) {
        boardContext.board.project_name = action.payload.projectName;
      }
    },
    updateCustomerSpecifications: (
      state,
      action: PayloadAction<{
        boardId: string;
        update: CustomerSpecificationsUpdate;
      }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      if (boardContext.board !== null) {
        boardContext.board.manufacturing_details.customer_specifications = {
          ...boardContext.board.manufacturing_details.customer_specifications,
          xray_inspection: action.payload.update.xray_inspection,
          cleaning: action.payload.update.cleaning,
          notes: action.payload.update.notes,
          conformal_coating: action.payload.update.conformal_coating,
          include_date_code: action.payload.update.include_date_code,
        };
        boardContext.customerSpecificationsUpdate = action.payload.update;
      }
    },
    clearCustomerSpecificationsUpdate: (
      state,
      action: PayloadAction<{ boardId: string }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      boardContext.customerSpecificationsUpdate = null;
    },
    removeShipmentInStore: (
      state,
      action: PayloadAction<{
        boardId: string;
        shipmentId: string;
      }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      delete boardContext.shipments[action.payload.shipmentId];
    },
    updateShipment: (
      state,
      action: PayloadAction<{
        boardId: string;
        shipmentId: string;
        update: ShipmentUpdate;
      }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      const matchingShipment =
        boardContext.shipments[action.payload.shipmentId];
      if (matchingShipment !== undefined) {
        matchingShipment.num_boards = action.payload.update.num_boards;
        matchingShipment.ship_date = action.payload.update.ship_date;
        matchingShipment.base_lead_time_days =
          action.payload.update.base_lead_time_days;
        matchingShipment.lead_time_days = null;
      }
    },
    addShipment: (
      state,
      action: PayloadAction<{
        boardId: string;
        shipment: Shipment;
      }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      boardContext.shipments[action.payload.shipment.id] =
        action.payload.shipment;
    },
    updateBoardNicknameInStore: (
      state,
      action: PayloadAction<{ boardId: string; nickname: string }>,
    ) => {
      const boardContext = getContextForBoardId(
        state,
        action.payload.boardId,
        true,
      );
      if (boardContext.board !== null) {
        boardContext.board.internal_nickname = action.payload.nickname;
      }
    },
  },
});

export const {
  clearBoards,
  clearBoard,
  boardLoaded,
  imagesLoaded,
  updateFabDrawingsState,
  billOfMaterialsLoaded,
  updateBOMState,
  physicalDetailsLoaded,
  dismissDesignRuleCheck,
  updateGerberLayerMapping,
  updatePhysicalDetailsState,
  boardRevisionLoaded,
  updateBoardRevisionState,
  quoteLoaded,
  shipmentLoaded,
  shipmentsLoaded,
  updateShipment,
  addShipment,
  updateCustomQuoteRequest,
  updateQuoteState,
  updateShipmentsState,
  updateManufacturingStep,
  boardStatesLoaded,
  pageFailure,
  pageFailureResolved,
  boardNotFound,
  setView,
  addBOMUpdates,
  removeBOMUpdates,
  addUserToBoard,
  setProjectName,
  updateCustomerSpecifications,
  clearCustomerSpecificationsUpdate,
  removeShipmentInStore,
  setSendingBoardEvent,
  updateBoardNicknameInStore,
} = uploadedBoardSlice.actions;

export default uploadedBoardSlice.reducer;
